import VideoSourceMenu from './VideoSourceMenu';
import { preferredGateway, mediaUrl, parseMediaReference, useMediaConfig } from 'services/media';

import { useAuth } from "helpers/authContext";
import React, { useEffect, useRef, useState } from "react";
import { get_ipfs_metadata } from "services/db-service";

interface VideoPlayerProps {
  url: string;
  cover?: string;
  post?: any;
  autoplay?: boolean;
  onTimeUpdate?: (time: number, duration: number) => void;
}
const LANG_MAP: Record<string, string> = {
  "en": "English", "es": "Spanish", "fr": "French", "de": "German", 
  "it": "Italian", "pt": "Portuguese", "nl": "Dutch", "ru": "Russian",
  "zh": "Chinese", "ja": "Japanese", "ko": "Korean", "ar": "Arabic",
  "hi": "Hindi", "tr": "Turkish", "vi": "Vietnamese", "pl": "Polish",
  "uk": "Ukrainian", "id": "Indonesian", "ms": "Malay", "th": "Thai"
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url,
  cover,
  autoplay = false,
  onTimeUpdate,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const pendingSourceSeek = useRef<{ cid: string; time: number; playing: boolean } | null>(null);
  const progressRef = useRef<HTMLDivElement | null>(null);
  const { app_settings, updateAppSettings } = useAuth(); 
  const mediaConfiguration = useMediaConfig();
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isGateWayRotated, setIsGateWayRotated] = useState(false);
  const stalledTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Custom Controls State
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [progress, setProgress] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubTimeText, setScrubTimeText] = useState("");
  const isScrubbingRef = useRef<boolean>(false);
  
  // Pro Controls State
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Subtitle State
  const [showSubtitles, setShowSubtitles] = useState(false);
  const [currentSubtitle, setCurrentSubtitle] = useState("");
  const [subtitleTracks, setSubtitleTracks] = useState<{
    original: { cues: any[], label: string, code: string },
    en: { cues: any[], label: string } | null
  }>({
    original: { cues: [], label: 'Original', code: 'unk' },
    en: null
  });
  const [activeTrack, setActiveTrack] = useState<'original' | 'en'>('original');
  
  const currentCues = activeTrack === 'en' ? subtitleTracks.en?.cues : subtitleTracks.original.cues;
  const hasSubs = (subtitleTracks.original.cues.length > 0) || (subtitleTracks.en?.cues && subtitleTracks.en.cues.length > 0);

  const parseSRT = (srt: string) => {
    try {
      const cues: {start: number, end: number, text: string}[] = [];
      const blocks = srt.trim().split(/\n\s*\n/);
      const parseTime = (t: string) => {
        const parts = t.split(',');
        const hms = parts[0].split(':').map(Number);
        const ms = Number(parts[1] || 0) / 1000;
        return hms[0] * 3600 + hms[1] * 60 + hms[2] + ms;
      };
      for (const block of blocks) {
        const lines = block.split('\n');
        const timeLine = lines.find(l => l.includes('-->'));
        if (timeLine) {
          const times = timeLine.match(/(\d{2}:\d{2}:\d{2},\d{3}) --> (\d{2}:\d{2}:\d{2},\d{3})/);
          if (times) {
            const start = parseTime(times[1]);
            const end = parseTime(times[2]);
            const textLines = lines.slice(lines.indexOf(timeLine) + 1);
            const text = textLines.join(' ').replace(/<[^>]*>/g, '').trim();
            if (text) cues.push({ start, end, text });
          }
        }
      }
      return cues;
    } catch (e) {
      console.error("[VideoPlayer] Error parsing SRT", e);
      return [];
    }
  };

  useEffect(() => {
    const selected = preferredGateway(app_settings?.gateway);
    if (selected && app_settings?.gateway !== selected) updateAppSettings({ ...app_settings, gateway: selected });
    setGateway(selected); setIsGateWayRotated(false); setHasError(false);
  }, [url, app_settings?.gateway, mediaConfiguration]);

  // Load subtitles from DB when CID is available
  useEffect(() => {
    const cid = getCIDPath()?.cid;
    if (!cid) return;
    get_ipfs_metadata(cid)
      .then((cache: any) => {
        const newTracks: any = {
           original: { cues: [], label: 'Original', code: cache.language || 'unk' },
           en: null
        };

        if (cache?.language) {
          newTracks.original.label = LANG_MAP[cache.language] || cache.language.toUpperCase();
        }

        if (cache?.srt_content && cache.srt_content.trim() !== "") {
          newTracks.original.cues = parseSRT(cache.srt_content);
        }

        // If original is NOT English, and we have English SRT
        if (cache?.language !== 'en' && cache?.srt_content_en && cache.srt_content_en.trim() !== "") {
          newTracks.en = {
            label: 'English',
            cues: parseSRT(cache.srt_content_en)
          };
        }

        setSubtitleTracks(newTracks);
        
        if (newTracks.original.cues.length > 0 || (newTracks.en && newTracks.en.cues.length > 0)) {
          setShowSubtitles(true);
        }
      })
      .catch((e: any) => console.log("[VideoPlayer] Could not load subtitles", e));
  }, [url]);

  const getCIDPath = () => parseMediaReference(url);
  const cidPath = getCIDPath();
  const [gateway, setGateway] = useState(() => preferredGateway(app_settings?.gateway));
  const finalUrl = mediaUrl(url, gateway, !isGateWayRotated);

  const handleVideoError = () => {
    // Clear stall timer in case error fires right after a stall
    if (stalledTimerRef.current) clearTimeout(stalledTimerRef.current);
    if (!isGateWayRotated && finalUrl.includes('/stream/')) {
      console.log("Cache route unavailable; trying direct R2 playback.");
      setIsGateWayRotated(true);
      if (videoRef.current) {
        setTimeout(() => { videoRef.current?.load(); }, 100);
      }
      return;
    }

    if (gateway !== mediaConfiguration.r2PublicBaseUrl) {
      if (!pendingSourceSeek.current && cidPath && videoRef.current) {
        pendingSourceSeek.current = { cid: cidPath.cid, time: videoRef.current.currentTime || 0, playing: isPlaying };
      }
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      setHasError(false);
      setGateway(mediaConfiguration.r2PublicBaseUrl);
      updateAppSettings({ ...app_settings, gateway: mediaConfiguration.r2PublicBaseUrl });
      return;
    }
    let msg = "Error loading video.";
    if (videoRef.current && videoRef.current.error) {
      const error = videoRef.current.error;
      switch (error.code) {
        case 1: msg = "Playback aborted."; break;
        case 2: msg = "Network error. Check your connection or gateway."; break;
        case 3: msg = "Video decoding failed."; break;
        case 4: msg = "Video not found (404) or format not supported."; break;
      }
    }
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    errorTimeoutRef.current = setTimeout(() => {
      setHasError(true);
      setErrorMessage(msg);
      setIsPlaying(false);
    }, 4000); // Wait 4 seconds before showing error
  };


  const handleTimeUpdate = () => {
    if (videoRef.current && onTimeUpdate) {
      onTimeUpdate(videoRef.current.currentTime, videoRef.current.duration || 0);
    }
    
    if (videoRef.current && !isScrubbingRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      if (duration) {
        const percent = (currentTime / duration) * 100;
        setProgress(percent);
      }

      // Subtitle sync
      if (showSubtitles && currentCues && currentCues.length > 0) {
        const cue = currentCues.find(c => currentTime >= c.start && currentTime <= c.end);
        const text = cue ? cue.text : "";
        if (text !== currentSubtitle) setCurrentSubtitle(text);
      } else if (currentSubtitle !== "") {
        setCurrentSubtitle("");
      }
    }
  };

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const updateProgressFromEvent = (e: React.PointerEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
    if (progressRef.current && videoRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      let clickX = e.clientX - rect.left;
      clickX = Math.max(0, Math.min(clickX, rect.width)); 
      const percentage = (clickX / rect.width) * 100;
      const duration = videoRef.current.duration;
      const newTime = (percentage / 100) * duration;
      if (isFinite(newTime)) {
        videoRef.current.currentTime = newTime;
        setProgress(percentage);
        setScrubTimeText(`${formatTime(newTime)} / ${formatTime(duration)}`);
      }
    }
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    isScrubbingRef.current = true;
    setIsScrubbing(true);
    updateProgressFromEvent(e);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isScrubbingRef.current) {
      e.preventDefault();
      updateProgressFromEvent(e);
    }
  };

  const handlePointerUpOrLeave = () => {
    if (isScrubbingRef.current) {
      isScrubbingRef.current = false;
      setIsScrubbing(false);
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (video.paused) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((err) => {
            if (err.name === 'AbortError') {
              console.log("⏯ Play interrupted safely.");
            } else {
              console.error("Play failed", err);
            }
          });
      }
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const formatCurrentTime = () => {
    if (videoRef.current) {
         return `${formatTime(videoRef.current.currentTime)} / ${formatTime(videoRef.current.duration)}`
    }
    return "0:00 / 0:00";
  }

  // Pro features handlers
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      if (newVolume === 0) {
        setIsMuted(true);
        videoRef.current.muted = true;
      } else {
        setIsMuted(false);
        videoRef.current.muted = false;
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      setIsMuted(newMuted);
      videoRef.current.muted = newMuted;
      if (newMuted) {
        setVolume(0);
        videoRef.current.volume = 0;
      } else {
        setVolume(1);
        videoRef.current.volume = 1;
      }
    }
  };

  const handleSpeedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const speed = parseFloat(e.target.value);
    setPlaybackRate(speed);
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
    }
  };

  const toggleFullscreen = async () => {
    if (!wrapperRef.current) return;
    
    try {
      if (!document.fullscreenElement) {
        await wrapperRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error("Error attempting to toggle fullscreen:", err);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 2500);
  };

  const handleMouseLeave = () => {
    if (isPlaying) setShowControls(false);
  };


  if (cidPath) {
    return (
      <div 
        className="ipfs-video-wrapper pro-video-wrapper speem-standard-player" 
        style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }} 
        ref={wrapperRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onPointerDown={handleMouseMove}
      >
        <video
          ref={videoRef}
          src={finalUrl}
          playsInline
          crossOrigin="anonymous" 
          style={{
            maxWidth: "100%",
            background: "#000",
            aspectRatio: "16/9",
            cursor: "pointer"
          }}
          onClick={togglePlay}
          poster={cover}
          autoPlay={autoplay}
          onLoadedMetadata={event => {
            event.currentTarget.playbackRate = playbackRate;
            const pending = pendingSourceSeek.current;
            if (!pending || pending.cid !== cidPath?.cid) return;
            const video = event.currentTarget;
            video.currentTime = Number.isFinite(video.duration) ? Math.min(pending.time, Math.max(0, video.duration - 0.05)) : pending.time;
            pendingSourceSeek.current = null;
            if (pending.playing) void video.play().catch(() => setIsPlaying(false));
          }}
          onTimeUpdate={handleTimeUpdate}
          onError={handleVideoError}
          onPlay={() => {
            setIsPlaying(true);
            if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
            if (stalledTimerRef.current) clearTimeout(stalledTimerRef.current);
            setHasError(false);
          }}
          onCanPlay={() => {
            if (stalledTimerRef.current) clearTimeout(stalledTimerRef.current);
          }}
          onStalled={() => {
            if (stalledTimerRef.current) clearTimeout(stalledTimerRef.current);
            stalledTimerRef.current = setTimeout(() => {
              console.log("[VideoPlayer] Video stalled — let's wait a bit more...");
              // Removed .load() to prevent restart from 0:00 on slow connections
            }, 8000);
          }}
          onPause={() => setIsPlaying(false)}
        >
          Your browser does not support video.
        </video>

        {!isPlaying && !hasError && (
            <img
              src="/assets/img/play.png"
              alt="Play"
              className="video-play-icon"
              style={{ pointerEvents: 'none' }}
            />
        )}

        <div className={`pro-video-controls-overlay ${showControls || !isPlaying ? 'visible' : ''}`}>
          <div 
            className="progress-bar-video pro-progress-bar" 
            style={{width:"100%"}}
            ref={progressRef} 
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUpOrLeave}
            onPointerLeave={handlePointerUpOrLeave}
            onPointerCancel={handlePointerUpOrLeave}
            onClick={(e) => e.stopPropagation()}
          >
            {isScrubbing && (
              <div className="scrub-time-tooltip">
                {scrubTimeText}
              </div>
            )}
            <div
              className="progress-bar-video-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>

          <div className="pro-controls-row">
            <div className="pro-controls-left">
              <button className="pro-control-btn" aria-label={isPlaying ? "Pause video" : "Play video"} onClick={togglePlay}>
                <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'}`} />
              </button>
              <div className="pro-volume-container">
                <button className="pro-control-btn" aria-label={isMuted ? "Unmute video" : "Mute video"} onClick={toggleMute}>
                  <i className={`fas ${isMuted || volume === 0 ? 'fa-volume-mute' : volume < 0.5 ? 'fa-volume-down' : 'fa-volume-up'}`} />
                </button>
                <input 
                  type="range" 
                  className="pro-volume-slider" aria-label="Video volume" 
                  min="0" max="1" step="0.05" 
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange} 
                />
              </div>
              <span className="pro-time-display">{formatCurrentTime()}</span>
            </div>

            <div className="pro-controls-right">
              <select className="pro-speed-select" aria-label="Playback speed" value={playbackRate} onChange={handleSpeedChange}>
                <option value="0.5">0.5x</option>
                <option value="1">Normal</option>
                <option value="1.5">1.5x</option>
                <option value="2">2x</option>
              </select>
              {hasSubs && (
                <div className="pro-cc-group">
                  {subtitleTracks.en && (
                    <select 
                      className="pro-lang-select" 
                      value={activeTrack} 
                      onChange={(e) => setActiveTrack(e.target.value as any)}
                    >
                      <option value="original">{subtitleTracks.original.label}</option>
                      <option value="en">English</option>
                    </select>
                  )}
                  <button
                    className={`pro-control-btn pro-cc-btn${showSubtitles ? ' active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setShowSubtitles(v => !v); }}
                    title={showSubtitles ? 'Disable subtitles' : 'Enable subtitles'}
                  >
                    <i className="fas fa-closed-captioning" />
                  </button>
                </div>
              )}
              <button className="pro-control-btn" aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"} onClick={toggleFullscreen}>
                <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Subtitle overlay */}
        {showSubtitles && currentSubtitle && (
          <div className="vp-subtitle-overlay">
            <span className="subtitle-text">{currentSubtitle}</span>
          </div>
        )}

        {hasError && (
          <div className="video-error-placeholder no-absolute-center scroll-safe-overlay">
            <div className="error-content">
              <i className="fas fa-exclamation-triangle error-icon" />
              <p className="error-text">{errorMessage}</p>
              <button 
                className="btn btn-sm btn-outline-light mt-2 mr-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setHasError(false);
                  if (videoRef.current) {
                    videoRef.current.load();
                  }
                }}
              >
                <i className="fas fa-redo mr-1" /> Retry
              </button>
            </div>
          </div>
        )}

        <div className="standard-player-source"><VideoSourceMenu cid={cidPath.cid} value={gateway} onChange={source => {
          if (source === preferredGateway(gateway)) return;
          if (videoRef.current) pendingSourceSeek.current = { cid: cidPath.cid, time: videoRef.current.currentTime || 0, playing: !videoRef.current.paused };
          if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
          setHasError(false);
          setGateway(source);
          setIsGateWayRotated(false);
          updateAppSettings({ ...app_settings, gateway: source });
        }} /></div>
      </div>
    );
  }

  return <div>No video available</div>;
};

export default VideoPlayer;
